import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SavingsIcon from '@mui/icons-material/Savings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { financialAPI, ProjectionData } from '../services/api';

const ProjectionsView: React.FC = () => {
  const [projectionData, setProjectionData] = useState<ProjectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjectionData();
  }, []);

  const loadProjectionData = async () => {
    try {
      setLoading(true);
      const data = await financialAPI.getProjections();
      setProjectionData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load projection data');
      console.error('Projection load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatTimeframe = (months: number) => {
    if (months < 12) {
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (remainingMonths === 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  };

  const getProjectionStatus = (savingsRate: number) => {
    if (savingsRate > 0) {
      return { label: 'Growing', color: 'success' as const };
    } else if (savingsRate === 0) {
      return { label: 'Stable', color: 'warning' as const };
    } else {
      return { label: 'Declining', color: 'error' as const };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!projectionData) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No projection data available
      </Alert>
    );
  }

  const projectionStatus = getProjectionStatus(projectionData.monthly_savings);

  // Prepare chart data
  const chartData = projectionData.projections.map(projection => ({
    timeframe: projection.period || formatTimeframe(projection.months),
    'Net Worth': projection.projected_value,
    'Savings Added': projection.savings_added,
    months: projection.months,
  }));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Financial Projections
      </Typography>

      {/* Current Status Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
        gap: 3,
        mb: 4 
      }}>
        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Current Net Worth
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(projectionData.current_net_worth)}
                </Typography>
              </Box>
              <AccountBalanceIcon color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Monthly Savings Rate
                </Typography>
                <Typography variant="h5" sx={{ color: projectionStatus.color === 'success' ? '#4caf50' : projectionStatus.color === 'error' ? '#f44336' : '#ff9800' }}>
                  {formatCurrency(projectionData.monthly_savings)}
                </Typography>
              </Box>
              <SavingsIcon sx={{ color: projectionStatus.color === 'success' ? '#4caf50' : projectionStatus.color === 'error' ? '#f44336' : '#ff9800' }} />
            </Box>
          </CardContent>
        </Card>

        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Trend Status
                </Typography>
                <Chip 
                  label={projectionStatus.label}
                  color={projectionStatus.color}
                  icon={<TrendingUpIcon />}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Projection Chart */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Net Worth Projection Over Time
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timeframe" />
              <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Line type="monotone" dataKey="Net Worth" stroke="#2196f3" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Savings Accumulation Chart */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Savings Accumulation Over Time
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timeframe" />
              <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Bar dataKey="Savings Added" fill="#4caf50" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Projection Table */}
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Projections
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timeframe</TableCell>
                  <TableCell>Projected Net Worth</TableCell>
                  <TableCell>Total Savings Added</TableCell>
                  <TableCell>Growth from Current</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectionData.projections.map((projection, index) => {
                  const growth = projection.projected_value - projectionData.current_net_worth;
                  const growthPercentage = ((growth / projectionData.current_net_worth) * 100).toFixed(1);
                  
                  return (
                    <TableRow key={index}>
                      <TableCell>{projection.period || formatTimeframe(projection.months)}</TableCell>
                      <TableCell>{formatCurrency(projection.projected_value)}</TableCell>
                      <TableCell>{formatCurrency(projection.savings_added)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography component="span">
                            {formatCurrency(growth)}
                          </Typography>
                          <Typography component="span" color="textSecondary" sx={{ ml: 1 }}>
                            ({growthPercentage}%)
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {projectionData.monthly_savings < 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body1">
            <strong>Warning:</strong> Your monthly expenses exceed your income by {formatCurrency(Math.abs(projectionData.monthly_savings))}. 
            Consider reducing expenses or increasing income to improve your financial outlook.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ProjectionsView;
